<script lang="ts">
  import { onDestroy } from 'svelte';
  import { BLUR_CLICK_GRACE_MS } from '../../lib/autocomplete.svelte';
  import type { ExcludeStore } from '../../stores/exclude.svelte';
  import { chipKey, filterExcludeSuggestions, type Chip } from '../../lib/exclude';
  import { withUniqueKeys } from '../../lib/unique-keys';

  interface Properties {
    store: ExcludeStore;
    /** Fires after any chip mutation so the parent can rewrite the URL. */
    onChange?: () => void;
  }

  const { store, onChange }: Properties = $props();

  const uiState = $state<{
    query: string;
    isOpen: boolean;
    activeIndex: number;
    blurTimer: ReturnType<typeof setTimeout> | undefined;
  }>({
    query: '',
    isOpen: false,
    activeIndex: -1,
    blurTimer: undefined,
  });

  const suggestions = $derived<Chip[]>(
    filterExcludeSuggestions(store.linesCatalog, uiState.query, store.excludedKeys),
  );

  /** Chips keyed for `{#each}`: `chipKey` plus its occurrence. */
  const keyedChips = $derived(withUniqueKeys(store.chips, chipKey));
  /** Suggestions keyed for `{#each}`: `chipKey` plus its occurrence. */
  const keyedSuggestions = $derived(withUniqueKeys(suggestions, chipKey));

  function clearBlurTimer(): void {
    if (uiState.blurTimer === undefined) return;
    clearTimeout(uiState.blurTimer);
    uiState.blurTimer = undefined;
  }

  function pick(chip: Chip): void {
    store.addChip(chip);
    uiState.query = '';
    uiState.activeIndex = -1;
    onChange?.();
  }

  function drop(chipHash: string): void {
    store.remove(chipHash);
    onChange?.();
  }

  function onFocus(): void {
    clearBlurTimer();
    uiState.isOpen = true;
  }

  function onBlur(): void {
    clearBlurTimer();
    uiState.blurTimer = setTimeout(() => {
      uiState.isOpen = false;
      uiState.blurTimer = undefined;
    }, BLUR_CLICK_GRACE_MS);
  }

  function didHandleArrowNav(event: KeyboardEvent): boolean {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      uiState.activeIndex = (uiState.activeIndex + 1) % suggestions.length;
      return true;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      uiState.activeIndex = (uiState.activeIndex - 1 + suggestions.length) % suggestions.length;
      return true;
    }
    return false;
  }

  function didHandleCommit(event: KeyboardEvent): boolean {
    if (event.key === 'Enter' && uiState.activeIndex >= 0) {
      event.preventDefault();
      pick(suggestions[uiState.activeIndex]);
      return true;
    }
    if (event.key === 'Escape') {
      uiState.isOpen = false;
      return true;
    }
    return false;
  }

  function onKeydown(event: KeyboardEvent): void {
    if (suggestions.length === 0) return;
    if (didHandleArrowNav(event)) return;
    didHandleCommit(event);
  }

  onDestroy(clearBlurTimer);
</script>

<!-- Chip-based exclude widget. Type to search modes/lines; picked chips send
     as block_modes/block_lines on /v1/routes and persist in the URL. -->
<details class="excl">
  <summary>Avoid lines &amp; modes ({store.chips.length})</summary>
  <div class="excl-body">
    {#if store.chips.length > 0}
      <ul class="chip-list" aria-label="Excluded lines and modes">
        {#each keyedChips as entry (entry.key)}
          <li class="chip">
            <span class="chip-name">{entry.item.name}</span>
            <button
              type="button"
              class="chip-x"
              aria-label="Remove {entry.item.name}"
              onclick={() => {
                drop(chipKey(entry.item));
              }}>×</button
            >
          </li>
        {/each}
      </ul>
    {/if}
    <div class="excl-input-wrap">
      <input
        type="text"
        class="io-input"
        placeholder="Add a line or mode…"
        bind:value={uiState.query}
        onfocus={onFocus}
        onblur={onBlur}
        onkeydown={onKeydown}
      />
      {#if uiState.isOpen && suggestions.length > 0}
        <ul class="excl-suggestions" role="listbox">
          {#each keyedSuggestions as entry, index (entry.key)}
            <li>
              <button
                type="button"
                class="excl-suggestion"
                class:active={index === uiState.activeIndex}
                onmouseenter={() => {
                  uiState.activeIndex = index;
                }}
                onclick={() => {
                  pick(entry.item);
                }}
              >
                <span>{entry.item.name}</span>
                <span class="excl-kind"
                  >{entry.item.kind === 'mode' ? 'MODE' : entry.item.mode || 'LINE'}</span
                >
              </button>
            </li>
          {/each}
        </ul>
      {/if}
    </div>
  </div>
</details>

<style>
  .excl {
    margin-bottom: var(--space-md);
  }

  .excl summary {
    cursor: pointer;
    color: var(--color-fg-muted);
    font-size: var(--font-size-sm);
    padding: var(--space-sm) 0;
  }

  .excl summary:hover {
    color: var(--color-fg);
  }

  .excl-body {
    padding: var(--space-sm);
    background: var(--color-surface);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-sm);
  }

  .chip-list {
    list-style: none;
    padding: 0;
    margin: 0 0 var(--space-sm);
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-xs);
  }

  .chip {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background: var(--color-surface-alt);
    border: 1px solid var(--color-border);
    border-radius: 3px;
    padding: 2px 6px;
    font-size: var(--font-size-xs);
    color: var(--color-fg);
  }

  .chip-x {
    background: none;
    border: none;
    color: var(--color-fg-muted);
    cursor: pointer;
    font-size: 14px;
    padding: 0 4px;
    line-height: 1;
  }

  .chip-x:hover {
    color: var(--color-accent-red);
  }

  .excl-input-wrap {
    position: relative;
  }

  .excl-suggestions {
    list-style: none;
    padding: 0;
    margin: 2px 0 0;
    position: absolute;
    left: 0;
    right: 0;
    top: 100%;
    background: var(--color-surface);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-sm);
    max-height: 150px;
    overflow-y: auto;
    z-index: 100;
  }

  .excl-suggestion {
    width: 100%;
    background: none;
    border: none;
    color: var(--color-fg);
    padding: 6px 8px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;
    font-size: var(--font-size-sm);
    text-align: left;
  }

  .excl-suggestion:hover,
  .excl-suggestion.active {
    background: var(--color-surface-alt);
  }

  .excl-kind {
    color: var(--color-fg-muted);
    font-size: var(--font-size-xs);
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
</style>
